import React, { useCallback, useRef, useState } from 'react';
import { Select, Spin } from 'antd';
import { getUsers } from '@app/api/admin.api';
import { UserListModel } from '@app/models/UserModel';

interface UserPickerProps {
  value?: number;
  onChange?: (userId: number | undefined) => void;
  placeholder?: string;
  style?: React.CSSProperties;
}

/**
 * Debounced server-side user search, reusing the existing `/api/v1/user/`
 * endpoint rather than pulling every user into the browser.
 */
export const UserPicker: React.FC<UserPickerProps> = ({ value, onChange, placeholder, style }) => {
  const [options, setOptions] = useState<{ label: string; value: number }[]>([]);
  const [fetching, setFetching] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout>>();
  // Guards against a slow early request overwriting a later one's results.
  const requestId = useRef(0);

  const search = useCallback((term: string) => {
    if (timer.current) clearTimeout(timer.current);
    if (!term.trim()) {
      setOptions([]);
      return;
    }
    setFetching(true);
    timer.current = setTimeout(() => {
      const id = ++requestId.current;
      getUsers({ page: 1, search: term })
        .then((data) => {
          if (id !== requestId.current) return;
          setOptions(
            (data.results || []).map((row: UserListModel) => ({
              value: row.user.id,
              label: `${row.user.name || 'No name'} — ${row.user.email}`,
            })),
          );
        })
        .catch(() => {
          if (id === requestId.current) setOptions([]);
        })
        .finally(() => {
          if (id === requestId.current) setFetching(false);
        });
    }, 350);
  }, []);

  return (
    <Select
      showSearch
      allowClear
      value={value}
      style={{ width: '100%', ...style }}
      placeholder={placeholder || 'Search by name or email…'}
      filterOption={false}
      onSearch={search}
      onChange={onChange}
      notFoundContent={fetching ? <Spin size="small" /> : null}
      options={options}
    />
  );
};

export default UserPicker;
